import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CaseDefinitionService } from '../../../services/case-definition.service';
import { ProjectService } from '../../../services/project.service';
import { ArrayValue, ObjectValue, PrimitiveValue, UIConfig, UIConfigUnit } from '../json-unit.class';

@Injectable({
    providedIn: 'root',
})
export class EditorService {
    private projectService = inject(ProjectService);
    private caseDefService = inject(CaseDefinitionService);

    private $caseDefinitionSchema: BehaviorSubject<any> = new BehaviorSubject(undefined);
    private schemaPromise: Promise<any> | null = null;

    async getSchema() {
        await this._fetchAndSetSchema();
        return this.$caseDefinitionSchema.asObservable();
    }

    async getSchemaPromise(): Promise<any> {
        await this._fetchAndSetSchema();
        return firstValueFrom(this.$caseDefinitionSchema.asObservable());
    }
    private async _fetchAndSetSchema() {
        if (this.schemaPromise) {
            return this.schemaPromise;
        }

        if (this.$caseDefinitionSchema.value === undefined) {
            const project = this.projectService.fetchCurrentProject();
            this.schemaPromise = firstValueFrom(this.caseDefService.fetchCaseDefinitions(project.projectId))
                .then((caseDefData) => {
                    this.$caseDefinitionSchema.next((caseDefData as any)?.caseSchema || null);
                    this.schemaPromise = null;
                })
                .catch((error) => {
                    this.schemaPromise = null; // Reset the promise in case of an error
                    console.log(error);
                });
        }

        return this.schemaPromise;
    }

    setSchema(schema: any) {
        this.$caseDefinitionSchema.next(schema);
    }

    /**
     * Reutrns the UI config for the json schema obj of type object
     * @param objSchema
     * @returns ui config
     */
    schemaToUIConfig(objSchema: any): UIConfig {
        let props = objSchema?.properties || {};
        let config = new UIConfig();

        Object.keys(props).forEach((propName: string) => {
            let value: PrimitiveValue | ObjectValue | ArrayValue;
            let required = objSchema.required?.includes(propName) || false;
            let defaultValue = props[propName].default || null;
            let valueType = props[propName].type;

            if (valueType === 'object') {
                value = new ObjectValue(propName, required);
                value.children = this.schemaToUIConfig(props[propName]).children;
            } else if (valueType === 'array') {
                value = new ArrayValue(propName, JSON.stringify(defaultValue), required);
                value.children = this._constructArrayConfig(props[propName]).children;
            } else {
                if (props[propName]?.format === 'date' && props[propName]?.type === 'string')
                    value = new PrimitiveValue(propName, 'date', defaultValue, required);
                else value = new PrimitiveValue(propName, props[propName].type, defaultValue, required);
            }

            config.addChild(value);
        });

        return config;
    }

    /**
     * Converts a json schema object of type array into UI config
     * @param arraySchema
     * @returns ui config
     */
    private _constructArrayConfig(arraySchema: any): UIConfig {
        let arrayItem = Array.isArray(arraySchema.items) ? arraySchema.items[0] : arraySchema.items;

        let config: UIConfig = new UIConfig();
        if (typeof arrayItem !== 'object' || !Object.keys(arrayItem).length) {
            return config;
        }

        let value: PrimitiveValue | ObjectValue | ArrayValue;
        if (arrayItem.type === 'array') {
            value = new ArrayValue('', arrayItem.default);
            value.children = this._constructArrayConfig(arrayItem).children;
        } else if (arrayItem.type === 'object') {
            value = new ObjectValue('', arrayItem.default);
            value.children = this.schemaToUIConfig(arrayItem).children;
        } else {
            if (arrayItem.format === 'date' && arrayItem.type == 'string') value = new PrimitiveValue('', 'date');
            else value = new PrimitiveValue('', arrayItem.type);
        }

        config.children = [value];
        return config;
    }

    /**
     * Converts the UI config to Json Schema
     * @param config
     * @returns
     */
    configToSchema(config: UIConfig) {
        let schema = {
            $schema: 'https://json-schema.org/draft/2020-12/schema',
            title: 'Case Definition',
        };

        schema = { ...schema, ...this._constructObjSchemaUnits(config.children) };

        return schema;
    }

    private _constructObjSchemaUnits(children: Array<UIConfigUnit>): object {
        let obj: {
            type: 'string' | 'integer' | 'boolean' | 'object' | 'array';
            properties: { [key: string]: any };
            default?: string | boolean | number | null;
        } = {
            type: 'object',
            properties: {},
        };

        children.forEach((child) => {
            let value: any = {
                type: child.type,
            };

            if (child.type === 'date') {
                value['format'] = child.type;
                value.type = 'string';
            }

            // if (child.required && child.name) obj.required.push(child.name);

            if (child.type === 'object') {
                value = { ...value, ...this._constructObjSchemaUnits(child.children) };
            } else if (child.type === 'array') {
                value = {
                    ...value,
                    ...this._constructArraySchemaUnits(child.children),
                };
                if (child.defaultValue) {
                    value.default = JSON.parse(child.defaultValue);
                }
            } else {
                if (child.defaultValue) value.default = child.defaultValue;
            }

            obj.properties[child.name] = value;
        });

        return obj;
    }

    private _constructArraySchemaUnits(children: Array<UIConfigUnit>): object {
        let obj: {
            type: 'array';
            items: object;
            properties?: object;
        } = {
            type: 'array',
            items: {},
        };

        if (children.length) {
            obj.items = {
                type: children[0]?.type,
            };

            if (children[0].type === 'object') {
                obj.items = {
                    ...obj.items,
                    ...this._constructObjSchemaUnits(children[0]?.children),
                };
            } else if (children[0].type === 'array') {
                obj.items = {
                    ...obj.items,
                    ...this._constructArraySchemaUnits(children[0]?.children),
                };
            }
        }

        if (children[0]?.type === 'date') {
            obj.items = { type: 'string', format: 'date' };
        }

        return obj;
    }

    serialiseConfig(item: UIConfigUnit) {
        let value: UIConfigUnit;

        if (item.type === 'object') {
            value = new ObjectValue(item.name, item.required);
            value.children = item.children?.map((child) => this.serialiseConfig(child));
        } else if (item.type === 'array') {
            value = new ArrayValue(item.name, (item as ArrayValue).defaultValue, item.required);
            value.children = item.children?.map((child) => this.serialiseConfig(child));
        } else {
            value = new PrimitiveValue(item.name, item.type, item.required);
        }

        return value;
    }

    generateJsonataPaths(schema: { [key: string]: any }, generateFor: string, currentPath = '') {
        let paths: Array<{ path: string; type: string; format?: string }> = [];
        if (!schema || !schema['type']) {
            return paths;
        }

        if (schema['type'] === 'array') {
            paths = this.generateJsonataPaths(schema['items'], generateFor, currentPath);
            return paths;
        }

        if (schema['type'] === 'object' && schema['properties']) {
            for (const key in schema['properties']) {
                const property = schema['properties'][key];

                const newCurrentPath = currentPath.split('.').filter(Boolean).concat([key]).join('.');

                const { skipColumn, skipFilter, skipDescendant } = property._metadata_ || {};

                const skipSelf =
                    (generateFor === 'filter' && skipFilter === true) ||
                    (generateFor === 'column' && skipColumn === true);

                if (skipSelf && skipDescendant === true) {
                    continue;
                }

                if (!skipSelf) {
                    const obj: { path: string; type: string; format?: string } = {
                        path: newCurrentPath,
                        type: property.type,
                    };

                    if (property.format) {
                        obj.format = property.format;
                    }

                    paths.push(obj);
                }

                if (skipDescendant) {
                    continue;
                }

                if (property.type === 'object') {
                    paths = [...paths, ...this.generateJsonataPaths(property, generateFor, newCurrentPath)];
                }

                if (property.type === 'array' && property.items) {
                    paths = [...paths, ...this.generateJsonataPaths(property.items, generateFor, newCurrentPath)];
                }
            }
        }

        return paths;
    }
}
